import { apiClient, getErrorMessage } from '@/lib/apiClient';

/**
 * One state machine of an entity, as `GET /api/state-machines/{entity}` returns it. An entity
 * may carry several (Multiple-State-Machines-Per-Entity.md §6.3) — the endpoint answers with a
 * list, the primary first — so `stateMachine` names which one: the entity field the machine
 * drives ("status", "paymentStatus"), the value commands, rule triggers and core.transitions
 * name it by. There is no enum name here: nothing consumed one.
 */
export interface StateMachineDto {
  /** Entity name (e.g., "Order") */
  entityName: string;
  /** Module name (e.g., "Sales") */
  moduleName: string;
  /** The entity field this machine drives (e.g., "status", "paymentStatus") */
  stateMachine: string;
  /** Initial state name */
  initialState: string;
  /** List of terminal state names */
  terminalStates: string[];
  /** All states in the state machine */
  states: StateDto[];
  /** All transitions in the state machine */
  transitions: StateMachineTransitionDto[];
}

/** One (entity, state machine) pair, as the list endpoint enumerates them. */
export interface StateMachineSummaryDto {
  entityName: string;
  stateMachine: string;
}

/**
 * State information DTO
 */
export interface StateDto {
  /** State name (enum value) */
  name: string;
  /** Is this the initial state? */
  isInitial: boolean;
  /** Is this a terminal (final) state? */
  isTerminal: boolean;
}

/**
 * Transition information DTO
 */
export interface StateMachineTransitionDto {
  /** Source state name */
  from: string;
  /** Target state name */
  to: string;
  /** Transition key (e.g., "DRAFT_CONFIRMED") — unique across all of the entity's machines */
  key: string;
  /** Required permission (optional) */
  permission?: string;
}

/**
 * State Machine Service
 * Provides state machine metadata for the Business Rules Editor and the Process Map
 */
export class StateMachineService {
  private readonly baseUrl = '/api/state-machines';

  /**
   * The state machines of an entity — one per machine, the primary first.
   * Used for configuring OnTransition triggers.
   */
  async getStateMachines(entityName: string): Promise<StateMachineDto[]> {
    try {
      const response = await apiClient.get<StateMachineDto[]>(`${this.baseUrl}/${entityName}`);
      return response.data ?? [];
    } catch (error) {
      console.error('Error fetching state machines:', error);
      throw new Error(getErrorMessage(error));
    }
  }

  /**
   * Every state machine there is: one row per (entity, machine), entities alphabetically,
   * an entity's machines in declaration order.
   */
  async getAvailableStateMachines(): Promise<StateMachineSummaryDto[]> {
    try {
      const response = await apiClient.get<StateMachineSummaryDto[]>(this.baseUrl);
      return response.data || [];
    } catch (error) {
      console.error('Error fetching available state machines:', error);
      throw new Error(getErrorMessage(error));
    }
  }
}

// Export singleton instance
export const stateMachineService = new StateMachineService();
